import { Flatfile } from "@flatfile/api";
import { AttributeDataType, CustomField } from "@wingspanhq/wingspan-api-sdk";

import {
  CONTRACTORS_SHEET_NAME,
  CONTRACTORS_SHEET_SLUG
} from "../../constants";

/** Prefix that keeps a custom-field column from colliding with a fixed one. */
export const CUSTOM_FIELD_KEY_PREFIX = "customField:";

/**
 * The spreadsheet contract users are shown on the start screen
 * (`ContractorColumnsStackedView`), expressed as a Flatfile blueprint.
 *
 * v1 built this workbook server-side, in the Flatfile listener that pushed rows
 * into the v1 API. v3 has no Flatfile integration at all — the browser parses
 * the sheet and posts each row to `POST /v3/platform/batches/{id}/items` — so
 * the blueprint lives here.
 *
 * TODO(v3-blocker): columns v1 accepted and v3 has no slot for are deliberately
 * absent rather than collected and discarded — W-9 tax details (SSN/EIN, legal
 * name, company structure, date of birth, address), the per-row engagement
 * name, the organization-account column that routed a contractor into a
 * sub-account, and `collaboratorStatus` (v1 used it to reactivate an archived
 * contractor on import). `PayeeImportBatchItemData` carries none of them and
 * the endpoint 422s unknown keys, so offering the columns would fail the whole
 * row. The engagement is not lost, only coarser: it moved to
 * `_configuration.engagementId`, one per batch, chosen on the start screen.
 * See V3_MIGRATION_TRACKER.md → /v3/platform/batches.
 */
export const buildContractorImportSheetConfig = (
  customFields: CustomField[]
): Flatfile.SheetConfig => ({
  name: CONTRACTORS_SHEET_NAME,
  slug: CONTRACTORS_SHEET_SLUG,
  allowAdditionalFields: true,
  fields: [
    {
      key: "email",
      label: "Email",
      type: "string",
      description:
        "Identifies the contractor. Provide this, an external ID, or a Wingspan user ID."
    },
    {
      key: "contractorId",
      label: "External contractor ID",
      type: "string",
      description: "Your own ID for the contractor."
    },
    {
      key: "payeeId",
      label: "Wingspan user ID",
      type: "string",
      description: "Wingspan's ID for an existing contractor."
    },
    {
      key: "firstName",
      label: "First name",
      type: "string",
      description: "Required when the row creates a new contractor."
    },
    {
      key: "lastName",
      label: "Last name",
      type: "string",
      description: "Required when the row creates a new contractor."
    },
    { key: "businessName", label: "Business name", type: "string" },
    { key: "phoneNumber", label: "Phone number", type: "string" },
    ...customFields.flatMap((field): Flatfile.Property[] => {
      if (!field.key) {
        return [];
      }

      return [
        {
          key: `${CUSTOM_FIELD_KEY_PREFIX}${field.key}`,
          label: field.name ?? field.key,
          type:
            field.dataType === AttributeDataType.BOOLEAN ? "boolean" : "string",
          ...(field.isRequired ? { constraints: [{ type: "required" }] } : {})
        }
      ];
    })
  ]
});
